import { TabulatorFull } from "tabulator-tables";



import { Factory } from "@/data/dao/Factory";
import { OrderDao } from "@/data/dao/service/OrderDao";
import { type CancellationState, OrderState } from "@/data/model/Enums";
import type { IOrder } from "@/data/model/Order";
import type { IOrdersQry } from "@/data/queries/order/OrdersQry";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";



import { logError } from "@/shared/logging/errors";
import { toMoneyWithSign } from "@/shared/utils/format";



import { Popup } from "@/components/dialog/Popup";



import { OperatorClient } from "@/operator/OperatorClient";
import { ProductionContext } from "@/operator/context/ProductionContext";
import { CancellationStateBadge } from "@/operator/elements/CancellationStateBadge";
import { OrderStateBadge } from "@/operator/elements/OrderStateBadge";
import { operatorEvents } from "@/operator/events/operatorEvents";
import { trans } from "@/operator/localization/Trans";
import { OrderDetailsModal } from "@/operator/modal/OrderDetailsModal";
import { UserMenu } from "@/operator/ui/UserMenu";
import { FulfillmentSoundNotifications } from "@/operator/ux/SoundNotifications";



import { BaseController, ViewTransition } from "./BaseController";
import DeliveryAddressForm from "@/components/pay/DeliveryAddressForm.vue";
import { translations } from "@/shared/localization/translations";


class OrderTableRow {
    constructor(
        public id: string,
        public date: string,
        public orderNumber: string,
        public state: OrderState | undefined,
        public cancellationState: CancellationState | undefined,
        public useCase: string | undefined,
        public tableName: string | undefined,
        public totalIncTip: string,
        public effectiveReadyDateTime: Date | undefined,
        public scheduledFulfillmentDateTime: Date | undefined,
        public userName: string | undefined
    ) {
        this.id = id;
        this.date = date;
        this.orderNumber = orderNumber;
        this.state = state;
        this.cancellationState = cancellationState;
        this.useCase = useCase;
        this.tableName = tableName;
        this.totalIncTip = totalIncTip;
        this.effectiveReadyDateTime = effectiveReadyDateTime;
        this.scheduledFulfillmentDateTime = scheduledFulfillmentDateTime;
        this.userName = userName;
    }
}

export class OrdersController extends BaseController {
    private productionContext: ProductionContext;

    private data: OrderTableRow[] = [];
    private table?: any;

    private readonly $head: JQuery;
    private readonly $filterHeader: JQuery;
    private readonly orderDao: OrderDao;

    private readonly $ordersSearch: JQuery;
    private readonly $searchButton: JQuery;

    private readonly $ordersTable: JQuery;
    private readonly $scrollable: JQuery;
    private $scrollableTable?: JQuery = undefined;

    private searchedOrderName: string = "";

    private readonly itemsPageSize = 30;
    private readonly createdFromLastBusinessDays = 3;
    private readonly orderStatesFilter = [
        OrderState.FullyPaid,
        OrderState.Closed,
        OrderState.ClosedNotPaid,
        OrderState.NotPaid,
    ];

    private currentPage = 0;
    private hasNextPage = false;
    private isDataLoading = false;

    constructor() {
        super("orders");

        this.productionContext = ProductionContext.getInstance();

        this.orderDao = Factory.getInstance<OrderDao>(OrderDao);
        this.$head = $('<div class="head"><div class="logo">tastier</div></div>').appendTo(this.$view);
        new UserMenu().appendTo(this.$head);

        this.$scrollable = $('<div class="scrollable"></div>');
        this.$scrollable.appendTo(this.$view);

        this.$filterHeader = $('<div class="filter-header" style="min-height: 100px; margin: 16px;"></div>').appendTo(
            this.$scrollable
        );
        const firstRow = $('<div class="row"></div>').appendTo(this.$filterHeader);

        this.$ordersSearch = $(
            `<input type="text" name="name" class="form-control col-md" placeholder="${trans.items.searchTerm}"/>`
        ).appendTo($('<div class="col-md-3"></div>').appendTo(firstRow));

        this.$searchButton = $(`<button class="btn btn-success">${trans.items.search}</button>`).appendTo(
            $('<div class="col-md"></div>').appendTo(firstRow)
        );
        this.$searchButton.bind("click.b", () => {
            this.searchedOrderName = this.$ordersSearch.val();
            this.reloadData();
        });

        this.$ordersSearch.keypress((event) => {
            const keycode = event.keyCode ? event.keyCode : event.which;
            if (keycode == 13) {
                // the enter key code
                this.searchedOrderName = this.$ordersSearch.val();
                this.reloadData();
                return false;
            }
        });
        this.$ordersTable = $('<div id="orders-table"></div>');
        this.$ordersTable.appendTo(this.$scrollable);
    }

    public showView(transition?: ViewTransition) {
        if (!this.isVisible()) {
            this.show(transition);
        }

        this.initializeTable();
    }

    private initializeTable() {
        this.table = new TabulatorFull("#orders-table", {
            maxHeight: "100%",
            height: "calc(100% - 144px)",
            data: this.data,
            layout: "fitColumns",
            layoutColumnsOnNewData: true,
            renderHorizontal: "virtual",
            columns: [
                {
                    title: trans.orders.date,
                    field: "date",
                    resizable: "header",
                    widthGrow: 1,
                    minWidth: 170,
                    sorter: "string",
                    headerSort: false,
                    frozen: true,
                },
                {
                    title: trans.orders.orderNumber,
                    field: "orderNumber",
                    resizable: "header",
                    sorter: "string",
                    minWidth: 130,
                    widthGrow: 1,
                    headerSort: false,
                },
                {
                    title: trans.orders.state,
                    field: "state",
                    resizable: "header",
                    sorter: "string",
                    widthGrow: 1,
                    minWidth: 130,
                    headerSort: false,
                    formatter: function (cell) {
                        const cellValue = cell.getValue();
                        if (cellValue == null) return "";
                        return OrderStateBadge.render(cell.getValue());
                    },
                },
                {
                    title: trans.orders.cancellationState,
                    field: "cancellationState",
                    resizable: "header",
                    sorter: "string",
                    minWidth: 130,
                    widthGrow: 1,
                    headerSort: false,
                    formatter: function (cell) {
                        const cellValue = cell.getValue();
                        if (cellValue == null) return "";

                        return CancellationStateBadge.render(cellValue);
                    },
                },
                {
                    title: trans.orders.useCase,
                    field: "useCase",
                    resizable: "header",
                    sorter: "string",
                    minWidth: 130,
                    widthGrow: 1,
                    headerSort: false,
                    formatter: function (cell) {
                        const cellValue = cell.getValue();
                        const id = cell.getData().id;
                        if (id === "loading") {
                            return '<div class="loader green" style="margin: auto;" />';
                        } else {
                            return cellValue;
                        }
                    },
                },
                {
                    title: trans.orders.tableName,
                    field: "tableName",
                    resizable: "header",
                    minWidth: 130,
                    sorter: "string",
                    widthGrow: 1,
                    headerSort: false,
                },
                {
                    title: trans.orders.scheduled,
                    field: "scheduledFulfillmentDateTime",
                    resizable: "header",
                    minWidth: 160,
                    sorter: "string",
                    widthGrow: 1,
                    headerSort: false,
                    formatter: function (cell) {
                        const preorderDateTime = cell.getValue();
                        if (preorderDateTime != null) {
                            return DateTime.toPreciseDateTimeString(preorderDateTime);
                        } else {
                            return "";
                        }
                    },
                },
                {
                    title: trans.orders.ready,
                    field: "effectiveReadyDateTime",
                    resizable: "header",
                    minWidth: 160,
                    sorter: "string",
                    widthGrow: 1,
                    headerSort: false,
                    formatter: function (cell) {
                        const effectiveReadyDateTime = cell.getValue();
                        if (effectiveReadyDateTime != null) {
                            return DateTime.toPreciseDateTimeString(effectiveReadyDateTime);
                        } else {
                            return "-";
                        }
                    },
                },
                {
                    title: trans.orderDetails.total,
                    field: "totalIncTip",
                    resizable: "header",
                    hozAlign: "right",
                    minWidth: 150,
                    sorter: "string",
                    widthGrow: 1,
                    headerSort: false,
                },
                {
                    title: trans.orders.user,
                    field: "userName",
                    resizable: "header",
                    hozAlign: "right",
                    minWidth: 160,
                    sorter: "string",
                    widthGrow: 1,
                    headerSort: false,
                    formatter: function (cell) {
                        const cellValue = cell.getValue();
                        return cellValue == "Anonymer User" ? trans.orders.anonymousUser : cellValue;
                    },
                },
            ],
        });

        this.table.on("scrollVertical", () => {
            if (!this.hasNextPage) {
                return;
            }

            if (this.$scrollableTable != null) {
                const bottom = this.$scrollableTable?.scrollTop() + this.$scrollableTable?.innerHeight();
                const scrollHeight = this.$scrollableTable[0].scrollHeight - 40;
                if (bottom >= scrollHeight) {
                    this.reloadData(this.currentPage + 1);
                }
            }
        });

        this.table.on("rowClick", (e, row) => {
            const rowData = row.getData();
            if (rowData.id === "loading") return;
            this.showDetails(rowData.id, rowData.orderNumber);
        });

        this.table.on("tableBuilt", () => {
            this.$scrollableTable = $("#orders-table > .tabulator-tableholder");
            this.refreshData();
        });
    }

    protected onBecomeVisible() {
        operatorEvents.productionContext.FulfillmentAdded.add(FulfillmentSoundNotifications.playSoundIfNeeded, this);
        operatorEvents.productionContext.FulfillmentChanged.add(FulfillmentSoundNotifications.playSoundIfNeeded, this);

        operatorEvents.productionContext.FulfillmentAdded.add(this.refreshData, this);
        operatorEvents.productionContext.FulfillmentChanged.add(this.refreshDataDelayed, this);
        operatorEvents.productionContext.LinkedFulfillmentsChanged.add(this.refreshDataDelayed, this);
        operatorEvents.productionContext.FulfillmentRemoved.add(this.refreshData, this);
    }

    protected onBecomeInvisible() {
        operatorEvents.productionContext.FulfillmentAdded.remove(FulfillmentSoundNotifications.playSoundIfNeeded, this);
        operatorEvents.productionContext.FulfillmentChanged.remove(FulfillmentSoundNotifications.playSoundIfNeeded, this);

        operatorEvents.productionContext.FulfillmentAdded.remove(this.refreshData, this);
        operatorEvents.productionContext.FulfillmentChanged.remove(this.refreshDataDelayed, this);
        operatorEvents.productionContext.LinkedFulfillmentsChanged.remove(this.refreshDataDelayed, this);
        operatorEvents.productionContext.FulfillmentRemoved.remove(this.refreshData, this);

        this.table = undefined;
        this.isDataLoading = false;
        this.data = [];
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private showDetails(id: string, orderNumber: string) {
        new OrderDetailsModal(id, () => {}).show();
    }

    // todo: temporary workaround because full cancellation is not done when fulfillment updated event is received
    private refreshDataDelayed() {
        setTimeout(() => this.reloadData(this.currentPage), 1000);
    }

    private refreshData() {
        this.reloadData(0);
    }

    private reloadData(pageNumber = 0) {
        if (this.isDataLoading) {
            return;
        }

        if (pageNumber == 0) {
            this.addLoadingRow();
            this.data = [];
        }

        this.isDataLoading = true;
        this.orderDao
            .getData(this.createQuery(pageNumber))
            .then((result) => {
                this.hasNextPage = result.hasNext;
                this.currentPage = pageNumber;

                const mappedOrders = result.items.map(this.mapOrder);

                this.data = [...this.data, ...mappedOrders];

                if (this.table == null) {
                    logError("Table is not initialized when reloading data in OrdersController");
                    return;
                }

                this.table.deleteRow("loading");

                if (pageNumber == 0) {
                    this.table.setData(mappedOrders);
                } else {
                    this.table.addData(mappedOrders, false);
                }

                if (this.hasNextPage) {
                    this.addLoadingRow();
                }
            })
            .catch((ex) => {
                logError(ex);
                Popup.show({
                    title: trans.general.failed,
                    message: trans.general.operationFailed,
                    uniqueId: "operation-failed-popup",
                });

                if (this.table != null) {
                    this.table.deleteRow("loading");
                }
            })
            .finally(() => {
                this.isDataLoading = false;
            });
    }

    private addLoadingRow() {
        if (this.table != null) {
            const loader = new OrderTableRow(
                "loading",
                "",
                "",
                undefined,
                undefined,
                "",
                "",
                "",
                undefined,
                undefined,
                ""
            );
            this.table.addData(loader, false);
        }
    }

    private mapOrder(order: IOrder): OrderTableRow {
        const date = DateTime.toPreciseDateTimeString(order.confirmedDateTime ?? order.created);
        const orderNumber = order.dailyOrderIncrement?.toString() ?? order.orderNumber;
        const orderApplicationName = Format.htmlEscape(
            Format.translate(order.useCaseName, OperatorClient.context.settings!.location!.language)
        );
        const tableName = Format.htmlEscape(order.customTableName);
        const totalIncTip = toMoneyWithSign(order.consumption + order.tip + order.deliveryFee, true);

        return new OrderTableRow(
            order.id,
            date,
            orderNumber,
            order.state,
            order.cancellationState,
            orderApplicationName,
            tableName,
            totalIncTip,
            order.effectiveReadyDateTime,
            order.scheduledFulfillmentDateTime,
            order.userName
        );
    }

    private createQuery(pageNumber: number) {
        return {
            locationId: OperatorClient.context.settings!.location!.id,
            limit: this.itemsPageSize,
            term: this.searchedOrderName,
            createdFromLastBusinessDays: this.createdFromLastBusinessDays,
            states: this.orderStatesFilter,
            offset: this.itemsPageSize * pageNumber,
        } as IOrdersQry;
    }
}
